import { SavedShipmentResponseDataDTO } from "./response.types";
import { SeaReferenceTypesDTO } from "entities/shared/seaCard.types";
import { ApiShipmentType, DateTimeDTO, SearchReferenceDTO } from "entities/shared/shipmentCard.types";
import { DeepPartial, ValueOf } from "shared/lib/typeCheck.lib";

export type PreparedDataToUpdateCardReturnType = Pick<
  SavedShipmentResponseDataDTO,
  "reference" | "carrier_scac" | "carrier_name" | "status" | "equipments_count" | "tags" | "size_type"
> & {
  location_from: string;
  location_to: string;
  location_pin: {
    lat: number;
    lng: number;
  };
  date_from: DateTimeDTO;
  date_from_actual: boolean;
  date_to: DateTimeDTO;
  date_to_actual: boolean;
  date_to_predictive: DateTimeDTO | null;
  type: ValueOf<typeof SeaReferenceTypesDTO> | string;
};

export type SeaWhereQuery = Partial<
  Pick<
    SavedShipmentResponseDataDTO,
    "reference" | "carrier_scac" | "carrier_name" | "equipments_count" | "status" | "tags"
  >
> & {
  location?: Pick<SavedShipmentResponseDataDTO["location"], "from">;
  /**
   * Which list the cards belong to. Sea and air share one table in the back
   * office, so every read has to say which of the two it is asking for; the
   * API layer fills it in, callers do not.
   */
  shipment_type?: ValueOf<typeof ApiShipmentType>;
};

export type OrderQueryColumn =
  | keyof Pick<
      SavedShipmentResponseDataDTO,
      | "id"
      | "reference"
      | "type"
      | "shipment_uuid"
      | "snapshot_uuid"
      | "carrier_scac"
      | "carrier_name"
      | "status"
      | "equipments_count"
      | "updated_at"
      | "created_at"
    >
  | ("location_from" | "location_to" | "date_from" | "date_from_actual" | "date_to" | "date_to_actual");

export interface SeaOrderQuery {
  column: OrderQueryColumn;
  asc: boolean;
}

export type GetSingleCardParams = Pick<GetCardsQuery, "where">;

export interface GetCardsQuery {
  limit?: number | null;
  offset?: number | null;
  where?: SeaWhereQuery;
  order?: SeaOrderQuery[];
}

export type UpdateTagsField = string | string[];
interface UpdateTags {
  tags?: SavedShipmentResponseDataDTO["tags"] | ({ add?: UpdateTagsField } & { remove?: UpdateTagsField });
}

export type UpdateCardData = DeepPartial<Omit<PreparedDataToUpdateCardReturnType, "id" | "profile">> & UpdateTags;
export interface UpdateCardQuery {
  id: number;
  data: UpdateCardData;
}
export interface UpdateCardsQuery {
  where: SeaWhereQuery;
  update: UpdateCardData;
}

export interface FollowCardQuery
  extends Partial<
    Omit<SavedShipmentResponseDataDTO, "created_at" | "type" | "date" | "carrier_scac" | "carrier_name" | "tags">
  > {
  /**
   * Which mode the card was tracked in. Sea and air share one store, so the
   * back office cannot infer it from the payload.
   */
  shipment_type: ValueOf<typeof ApiShipmentType>;
  type: SearchReferenceDTO | null;
  carrier_scac: string | null;
  carrier_name: string | null;
  date: {
    from: DateTimeDTO | null;
    from_actual: boolean;
    to: DateTimeDTO | null;
    to_actual: boolean;
    to_predictive: DateTimeDTO | null;
  };
}
